/*
 * Pure display helpers for the facts the API returns about a product. Each
 * returns null when there is nothing honest to show, so callers render the
 * chip / row only when the field is really present.
 */
import { getLocale } from '../../i18n';
import type { ProductDetail } from '../../types/product';

// Kinguin writes "Other" into `platform` for anything it can't classify;
// that is "unknown", not a platform worth a chip.
const UNKNOWN_PLATFORM = 'Other';
/** Above this many countries a code list stops being readable as chips. */
const MAX_LISTED_REGIONS = 3;

export function knownPlatform(p: Pick<ProductDetail, 'platform'>): string | null {
  return p.platform && p.platform !== UNKNOWN_PLATFORM ? p.platform : null;
}

export function releaseDate(p: Pick<ProductDetail, 'releaseDate'>): Date | null {
  if (!p.releaseDate) return null;
  const d = new Date(p.releaseDate);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatReleaseDate(d: Date): string {
  return new Intl.DateTimeFormat(getLocale(), { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(d);
}

/** Year once released, the full date while the game is still upcoming. */
export function releaseChip(p: Pick<ProductDetail, 'releaseDate'>): string | null {
  const d = releaseDate(p);
  if (!d) return null;
  return d.getTime() > Date.now() ? formatReleaseDate(d) : String(d.getUTCFullYear());
}

// Numeric ratings (PEGI 3 / 7 / 12 / 18) get "+" appended for the badge.
// Letter or already-suffixed ratings (E, M, R, E10+, 12+, etc.) pass through.
export function ageLabel(p: Pick<ProductDetail, 'ageRating'>): string | null {
  const s = String(p.ageRating ?? '').trim();
  if (s === '') return null;
  return /^\d+$/.test(s) ? `${s}+` : s;
}

/** Few regions are listed by code; many collapse to a count of what the API sent. */
export function regionChip(p: Pick<ProductDetail, 'regions' | 'region'>, countLabel: (count: number) => string): string | null {
  if (p.region) return p.region;
  const regions = p.regions ?? [];
  if (regions.length === 0) return null;
  return regions.length <= MAX_LISTED_REGIONS ? regions.join(', ') : countLabel(regions.length);
}
